'use client';

import { useAuthContext } from '@/contexts/auth-context';
import { useAuthModal } from '@/contexts/auth-modal-context';

/**
 * Primary auth hook that provides:
 * - Authentication state (isAuthenticated, isLoading)
 * - User objects (authUser, supabaseUser, unifiedUser)
 * - Modal controls (openLoginModal, openSignupModal, closeModal)
 * - requireAuth helper for gated actions
 *
 * Use this hook when you need:
 * - To trigger login/signup modals
 * - To check if user is authenticated
 * - Access to raw Supabase Auth or DB user objects
 *
 * For user data with convenient getters (isPaidUser, userPlan, etc.),
 * use useAuthUser instead.
 *
 * @see useAuthUser for user data with computed properties
 */
export function useAuth() {
  const {
    isAuthenticated,
    isLoading,
    user,
    authUser,
    supabaseUser,
    isSupabaseUserLoading,
    supabaseUserError,
    refreshUserData,
  } = useAuthContext();

  const { openLoginModal, openSignupModal, closeModal, activeTab, isOpen } =
    useAuthModal();

  const requireAuth = (callback?: () => void, redirectUrl?: string) => {
    if (!isAuthenticated && !isLoading) {
      openLoginModal(redirectUrl);
      return false;
    }

    if (callback && isAuthenticated) {
      callback();
    }

    return isAuthenticated;
  };

  return {
    // Auth state
    isAuthenticated,
    isLoading,
    user: authUser, // Supabase Auth user object

    // Enhanced auth data
    unifiedUser: user, // The combined user object with DB data
    supabaseUser,
    isSupabaseUserLoading,
    supabaseUserError,

    // Actions
    requireAuth,
    refreshUserData,
    openLoginModal,
    openSignupModal,
    closeModal,
    activeTab,
    isOpen,
  };
}
